import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { TextFormatterIcon, TextFormatterNames } from '../../icons/TextFormatterIcons';
import { TabStyles } from '../../styles/TabStyles';
import { isMac } from '../../utils/BrowserDetection';
import { __UNSTABLE__INTERNAL__FocusIndicator as FocusIndicator } from '../../utils/FocusIndicator';
import { ButtonAttrs, DivAttrs } from '../../utils/HtmlAttrs';
import { wrap } from '../../utils/Math';
import { typedKeys } from '../../utils/TypedKeys';
import { ButtonType, ButtonWidget } from '../Button/ButtonWidgetShim';
import { Tooltip, TooltipPosition } from '../Tooltip/Tooltip';
import { applyMarkup, FormatterMap, FormatterType } from './MarkupHelpers';

export const TextFormatterToolbarHeight = 0; // 40;

const IndexAttr = 'data-toolbar-button-index';

const ToolbarButtonStyle = TypeStyle.style({
  padding: 4,
  $nest: {
    '&:disabled': { pointerEvents: 'none' },
    '& ~ &': { marginLeft: 8 },
  }
});

const ToolbarPanelStyleName = TypeStyle.style({
  alignItems: 'center',
  background: TabStyles.Colors.F2,
  border: `1px solid ${TabStyles.Colors.L5}`,
  borderBottom: 'none',
  boxSizing: 'border-box',
  display: 'flex',
  height: TextFormatterToolbarHeight,
  paddingBottom: 8,
  paddingLeft: 8,
  paddingTop: 8,
  position: 'relative',
});

export interface TextFormatterToolbarProps {
  activeFormatters: FormatterType[];
  disabled: boolean;
  textAreaId: string;
  textAreaElement: HTMLTextAreaElement | null;
}

export const TextFormatterToolbar: React.FC<TextFormatterToolbarProps & DivAttrs> = props => {
  const { activeFormatters, disabled, textAreaId, textAreaElement, ...restProps } = props;
  const [focusWithin, setFocusWithin] = React.useState<boolean>(false);
  const focusIndex = React.useRef<number>(0);
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  const handleBlur = React.useCallback<React.FocusEventHandler<HTMLDivElement>>(e => {
    setFocusWithin(false);
    restProps.onBlur?.(e);
  }, [restProps.onBlur]);

  const handleFocus = React.useCallback<React.FocusEventHandler<HTMLDivElement>>(e => {
    const activeButton = toolbarRef.current?.querySelector<HTMLElement>(`[${IndexAttr}="${focusIndex.current}"]`);
    setFocusWithin(true);
    activeButton?.focus();
    restProps.onFocus?.(e);
  }, [focusIndex.current, toolbarRef.current, restProps.onFocus]);

  const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLDivElement>>(e => {
    if (e.key === 'ArrowLeft') {
      focusIndex.current = wrap(focusIndex.current - 1, 0, Object.keys(FormatterMap).length - 1);
    } else if (e.key === 'ArrowRight') {
      focusIndex.current = wrap(focusIndex.current + 1, 0, Object.keys(FormatterMap).length - 1);
    } else {
      return;
    }

    handleFocus(e as unknown as React.FocusEvent<HTMLDivElement>);
    restProps.onKeyDown?.(e);
  }, [focusIndex.current, handleFocus, restProps.onKeyDown]);

  const handleMouseDown = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(e => {
    if (e.currentTarget === e.target) { e.preventDefault(); }
    restProps.onMouseDown?.(e);
  }, [restProps.onMouseDown]);

  return <FocusIndicator><div
    {...restProps}
    aria-controls={textAreaId}
    className={ToolbarPanelStyleName}
    onBlur={handleBlur}
    onFocus={handleFocus}
    onMouseDown={handleMouseDown}
    onKeyDown={handleKeyDown}
    ref={toolbarRef}
    role='toolbar'
    tabIndex={focusWithin ? -1 : 0}
  >
    {typedKeys(FormatterMap).map((type, index) => {
      return <ToolbarButton
        buttonIndex={index}
        disabled={disabled}
        formatterType={type}
        isToggled={activeFormatters.indexOf(type) !== -1}
        key={type}
        onMouseDown={() => focusIndex.current = index}
        textAreaElement={textAreaElement}
      />;
    }
    )}
  </div></FocusIndicator>;
};
TextFormatterToolbar.displayName = 'TextFormatterToolbar';

interface ToolbarButtonProps {
  buttonIndex: number;
  disabled?: boolean;
  formatterType: FormatterType;
  isToggled: boolean;
  textAreaElement: HTMLTextAreaElement | null;
}

// We call `preventDefault()` on MouseDown to prevent the selection in the textarea from clearing when interacting with the buttons
const preventDefault = (e: React.SyntheticEvent) => e.preventDefault();

const ToolbarButton: React.FC<ToolbarButtonProps & ButtonAttrs> = props => {
  const { buttonIndex, disabled, formatterType, isToggled, role, textAreaElement, ...restProps } = props;

  const shortcut = FormatterMap[formatterType].keyboardShortcut;
  const shortcutKeyLabel = shortcut?.keyLabel.toUpperCase();
  const ctrlKeyPrefix = `${isMac ? '⌘' : Messages.keyboardModifierCtrl()}`;
  const shiftKeyPrefix = shortcut?.shiftKey ? `${isMac ? '⇧' : Messages.keyboardModifierShift()}` : '';

  const getTooltipContent = () => <span>
    {TextFormatterNames[formatterType]()}
    {shortcutKeyLabel && <>
      <br />
      {isMac ? shiftKeyPrefix + ctrlKeyPrefix : ctrlKeyPrefix + shiftKeyPrefix}
      {shortcutKeyLabel}
    </>}
  </span>;

  const handleClick = React.useCallback(() => {
    if (textAreaElement) {
      applyMarkup(formatterType, textAreaElement);
    } else {
      throw new Error('Attempting to format a textArea, but none is available');
    }
  }, [formatterType, textAreaElement]);

  return <Tooltip getContent={getTooltipContent} position={TooltipPosition.Top}>
    <ButtonWidget
      {...restProps}
      aria-pressed={isToggled}
      buttonType={ButtonType.Icon}
      children={<TextFormatterIcon type={formatterType} />}
      className={TypeStyle.classes(ToolbarButtonStyle)}
      disabled={disabled}
      {...{ [IndexAttr]: buttonIndex }}
      onClick={handleClick}
      onMouseDown={preventDefault}
      tabIndex={-1}
      testId={formatterType}
    />
  </Tooltip>;
};
